/* eslint-disable jsx-a11y/alt-text */
import React, { useState, useEffect } from 'react';
import iconHamburger from '../icons/hamburger.svg';
import iconCross from '../icons/cross-icon.svg';
import iconTg1 from '../icons/tg-1.svg';
import iconX1 from '../icons/x-1.svg';
import { useAccount } from 'wagmi';
import { useWeb3Modal } from '@web3modal/wagmi/react';
import { Link } from 'react-router-dom';
import formatNumber, { imageUrl, apiUrl, coinNames, projectName } from '../utils/constants.ts';
import { useTheme } from '../context/ThemeContext';
import iconLight from '../icons/sun.svg';
import iconDark from '../icons/moon.svg';

// History flow logo:
// Backend stores some logos as tokenName-chainId-contract.png (e.g. "Privacy test-97-0x....png")
// and some as FlazePad-chainId-contract.png. If getHistory returns name: "FlazePad" for a token
// whose file is tokenName-..., we must try tokenName (or token) first so we never request FlazePad first.
const HistoryFlowLogo = ({ tokenName, name, token, chainId, contract }) => {
  const rawTokenName = (tokenName || '').trim();
  const rawName = (name || '').trim();
  const rawToken = (token || '').trim();
  const sanitized = (s) => (s || '').replace(/[^a-zA-Z0-9]/g, '');
  const isProjectName = (s) => !s || s === projectName;

  const build = (label, useEncode = false) =>
    imageUrl + 'logo/' + (useEncode ? encodeURIComponent(label) : label) + '-' + chainId + '-' + contract + '.png';

  const add = (label, useEncode = false) => {
    if (!label || isProjectName(label)) return;
    const url = build(label, useEncode);
    if (!variants.includes(url)) variants.push(url);
  };

  const variants = [];
  // When getHistory returns name "FlazePad" but backend file is tokenName-..., try known token name for this contract
  const knownLogoTokenNames = {
    '0xbe4b36c871d725b74e220213e83a047655245418': 'Privacy test',
  };
  const knownName = contract ? knownLogoTokenNames[contract.toLowerCase()] : null;
  if (knownName) {
    add(knownName, false);
    if (/[\s#%]/.test(knownName)) add(knownName, true);
  }
  // Token name format from API — try raw and encoded (server may expect "Privacy%20test" for "Privacy test")
  if (rawTokenName && !isProjectName(rawTokenName)) {
    add(rawTokenName, false);
    if (/[\s#%]/.test(rawTokenName)) add(rawTokenName, true);
  }
  if (rawName && !isProjectName(rawName)) {
    add(rawName, false);
    if (/[\s#%]/.test(rawName)) add(rawName, true);
  }
  if (rawToken && !isProjectName(rawToken)) {
    add(rawToken, false);
    if (/[\s#%]/.test(rawToken)) add(rawToken, true);
  }
  const sanitizedTokenName = rawTokenName ? sanitized(rawTokenName) : '';
  const sanitizedName = sanitized(rawName);
  const sanitizedToken = sanitized(rawToken);
  if (sanitizedTokenName) add(sanitizedTokenName, false);
  if (sanitizedName) add(sanitizedName, false);
  if (sanitizedToken && !isProjectName(sanitizedToken)) add(sanitizedToken, false);
  // FlazePad format last
  variants.push(build(projectName, false));

  const [variantIndex, setVariantIndex] = useState(0);

  useEffect(() => {
    setVariantIndex(0);
  }, [rawTokenName, rawName, rawToken, chainId, contract]);

  const handleError = () => {
    setVariantIndex((prev) =>
      prev + 1 < variants.length ? prev + 1 : prev
    );
  };

  return (
    <img
      key={variants[variantIndex]}
      src={variants[variantIndex]}
      width="24"
      height="24"
      alt=""
      className="ml-1.5 mr-2.5 rounded-full"
      onError={handleError}
    />
  );
};

const TopBar = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { address } = useAccount();
  const { theme, toggleTheme } = useTheme();
  const { open } = useWeb3Modal();

  // Toggle theme and save to localStorage
  const handleThemeToggle = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    toggleTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  const handleHamburgerClick = () => {
    setIsExpanded(!isExpanded);
  };
  const [history, setHistory] = useState([]);
  
  useEffect(() => {
    // Add event listener for window resize
    const handleResize = () => {
      if (window.innerWidth >= 640) {
        setIsExpanded(false);
      }
    };

    window.addEventListener('resize', handleResize);
    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const FetchData = async () => {
      try {
        await fetch(
          apiUrl + `/getHistory`,
          {
            method: 'GET'
          }
        ).then(async res => {
          let data = await res.json();
          if (data.length > 0) {
            let history;
            let historyData = [];
            for (let i = 0; i < data?.length; i++) {
              if (data[i].chainId !== undefined) {
                let chainId = data[i].chainId;
                let amount = data[i].amount;
                let buyer = data[i].buyer;
                let name = data[i].name;
                let token = data[i].token;
                let tokenName = data[i].tokenName; // use for logo filename when name is "FlazePad" (e.g. "Privacy test")
                let type = data[i].type;
                let contract = data[i].contract;
                let avatarUrl = imageUrl + "profile-" + data[i].buyer + ".png";
                history = { chainId, amount, buyer, name, token, tokenName, type, contract, avatarUrl };
                historyData.push(history);
              }
            }
            setHistory(historyData);
          }
        });
      } catch (e) {
        console.error(e);
      }
    };
    FetchData();
  }, []);

  let currentPath = window.location.pathname;

  return (
    <>
      <div className="w-full overflow-hidden  h-8 bg-[var(--section-bg)]">
        {history.length > 0 && (
          <div className='overflow-hidden  w-full h-full'>
            <div className="flex absolute top-20 left-0 h-24 gap-0.5 w-max animate-slide ">
              {history.map(({ chainId, amount, buyer, name, token, tokenName, contract, type, avatarUrl }, i) => (
                <div
                  className="flex relative justify-center items-center px-3 py-1 rounded bg-[var(--card-bg)] border border-transparent"
                  style={{
                    background: 'var(--card-bg) padding-box, var(--card-border) border-box',
                    border: '1px solid transparent',
                    borderRadius: 'var(--card-radius)'
                  }}
                  key={i}
                >
                  <Link
                    className="flex justify-center items-center no-underline text-sm ml-1.5"
                    style={{ color: 'var(--text-secondary)' }}
                    to={'/profile/?address=' + buyer}
                    target={currentPath.includes('profile') ? '_blank' : ''}
                    rel="noreferrer"
                  >
                    {buyer.slice(0, 4) + '...' + buyer.slice(-3)}
                  </Link>
                  <span
                    className="pl-3 text-sm"
                    style={{ color: type === "bought" ? 'var(--text-primary)' : '#000000' }}
                  >
                    {type === "bought" ? "+" : "-"}
                  </span>
                  <span
                    className="text-sm ml-1.5 inline-flex items-center gap-1"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    <span>
                      {formatNumber(Number(amount))} 
                    </span>
                    <span>
                      {type === 'bought' ? coinNames[chainId] : 'Token'}
                    </span>
                  </span>
                  <Link
                    className="flex items-center no-underline ml-1.5"
                    style={{ color: 'var(--text-secondary)' }}
                    to={`/buy/?chain=${chainId}&address=${contract}`}
                    rel="noreferrer"
                  >
                    <span
                      className="text-sm font-bold ml-1.5"
                      style={{ color: 'var(--text-accent)' }}
                    >
                      {name.length > 6 ? `${name.slice(0, 6)}...` : name}
                    </span>
                    <HistoryFlowLogo tokenName={tokenName ?? name} name={name} token={token} chainId={chainId} contract={contract} />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="fixed top-0 left-0 w-full z-50 py-3 px-4" style={{ background: 'var(--header-bg)' }}>
        <div className={`my-0 max-w-7xl mx-auto w-full items-center ${isExpanded ? 'pb-2' : ''}`}>
          <div className='flex justify-between'>
            <div className='flex flex-row items-center'>
              <Link 
                to="/dashboard" 
                className="cursor-pointer focus:outline-none active:outline-none flex items-center"
                style={{ outline: 'none', border: 'none', display: 'flex', alignItems: 'center', marginTop: '-16px' }}
                onMouseDown={(e) => e.preventDefault()}
              >
              <img
                src={theme === 'light' ? "/img/logo-light.png" : "/img/logo-dark.png"}
                className="h-20 w-auto"
                alt="LoadingCoin logo"
                  style={{ outline: 'none', border: 'none', display: 'block', verticalAlign: 'middle' }}
              />
              </Link>
              <div className="lg:flex hidden flex-row gap-4 ml-6 font-['Bricolage_Grotesque',_sans-serif] items-center">
                <Link to="/dashboard" className="px-4 py-2 rounded-lg transition-colors"
                  style={{
                    color: '#ffffff'
                  }}>
                  Dashboard
                </Link>
                <Link to="/create" className="px-4 py-2 rounded-lg transition-colors"
                  style={{
                    color: '#ffffff'
                  }}>
                  Create
                </Link>
                <Link to={`/profile/?address=${address}`} className="px-4 py-2 rounded-lg transition-colors"
                  style={{
                    color: '#ffffff'
                  }}>
                  Profile
                </Link>
                <Link to="/whitepaper" className="px-4 py-2 rounded-lg transition-colors"
                  style={{
                    color: '#ffffff'
                  }}>
                  Whitepaper
                </Link>
                {/* <Link to="/referral" className="px-4 py-2 rounded-lg transition-colors"
                  style={{
                    backgroundColor: currentPath === '/referral'
                      ? 'var(--nav-button-active-bg)'
                      : 'var(--nav-button-inactive-bg)',
                    color: currentPath === '/referral'
                      ? 'var(--nav-button-active-text)'
                      : 'var(--nav-button-inactive-text)'
                  }}>
                  Referral
                </Link> */}
                {/* <Link to="/faq" className="px-4 py-2 rounded-lg transition-colors"
                  style={{
                    backgroundColor: currentPath === '/faq'
                      ? 'var(--nav-button-active-bg)'
                      : 'var(--nav-button-inactive-bg)',
                    color: currentPath === '/faq'
                      ? 'var(--nav-button-active-text)'
                      : 'var(--nav-button-inactive-text)'
                  }}>
                  FAQ
                </Link> */}
                {/* <Link to="/about" className="px-4 py-2 rounded-lg transition-colors"
                  style={{
                    backgroundColor: currentPath === '/about'
                      ? 'var(--nav-button-active-bg)'
                      : 'var(--nav-button-inactive-bg)',
                    color: currentPath === '/about'
                      ? 'var(--nav-button-active-text)'
                      : 'var(--nav-button-inactive-text)'
                  }}>
                  About
                </Link> */}
                {/* <Link to="/presale" className="px-4 py-2 rounded-lg transition-colors"
                  style={{
                    backgroundColor: currentPath === '/presale'
                      ? 'var(--nav-button-active-bg)'
                      : 'var(--nav-button-inactive-bg)',
                    color: currentPath === '/presale'
                      ? 'var(--nav-button-active-text)'
                      : 'var(--nav-button-inactive-text)'
                  }}>
                  Presale
                </Link> */}
                {/* <Link to="/blog" className="px-4 py-2 rounded-lg transition-colors"
                  style={{
                    backgroundColor: currentPath === '/blog'
                      ? 'var(--nav-button-active-bg)'
                      : 'var(--nav-button-inactive-bg)',
                    color: currentPath === '/blog'
                      ? 'var(--nav-button-active-text)'
                      : 'var(--nav-button-inactive-text)'
                  }}>
                  Blogs
                </Link> */}
              </div>
            </div>
            <div className='flex flex-row items-center gap-4'>
              <div className='sm:flex hidden flex-row gap-4 items-center'>
                {/* <button onClick={handleThemeToggle} className="p-2">
                  {theme === 'light' ?
                    <img src={iconDark} className='w-6 h-6' alt="Dark mode" /> :
                    <img src={iconLight} className='w-6 h-6' alt="Light mode" />
                  }
                </button> */}
                {/* <a href="#" target="_blank" className="p-2" rel="noreferrer">
                  <img src={iconX1} className={`w-6 h-6 ${theme === 'light' ? 'filter invert' : ''}`} />
                </a> */}
                <a href="https://t.me/FlazePad" target="_blank" className="p-2" rel="noreferrer">
                  <img src={iconTg1} className={`w-6 h-6 ${theme === 'light' ? 'filter invert' : ''}`} />
                </a>
              </div>
              <div className="navConnectButtonBox">
                {typeof window !== 'undefined' && customElements.get('w3m-button') ? (
                <w3m-button />
                ) : (
                  <button 
                    onClick={() => open()}
                    style={{ 
                      padding: '8px 16px', 
                      background: '#000000', 
                      color: '#ffffff', 
                      borderRadius: '8px',
                      cursor: 'pointer',
                      border: '1px solid #333',
                      fontSize: '14px',
                      fontWeight: '500',
                      fontFamily: 'inherit'
                    }}
                  >
                    {address ? `${address.slice(0, 6)}...${address.slice(-4)}` : 'Connect Wallet'}
                  </button>
                )}
              </div>
              <button className='bg-black hover:bg-[#333333] rounded-full p-2 flex lg:hidden' onClick={handleHamburgerClick}>
                <img src={isExpanded ? iconCross : iconHamburger} className='w-8 h-8' />
              </button>
            </div>
          </div>
          {isExpanded && (
            <div className='relative bg-black rounded-[25px] flex flex-col gap-4 px-8 pt-10 pb-40 sm:p-12 w-full items-center overflow-hidden mt-4'>
              <div className='flex flex-col gap-4 w-full'>
                <Link to="/dashboard" className="px-4 py-3 rounded-lg text-center transition-colors"
                  style={{
                    color: '#ffffff'
                  }}>
                  Dashboard
                </Link>
                <Link to="/create" className="px-4 py-3 rounded-lg text-center transition-colors"
                  style={{
                    color: '#ffffff'
                  }}>
                  Create Token
                </Link>
                <Link to={`/profile/?address=${address}`} className="px-4 py-3 rounded-lg text-center transition-colors"
                  style={{
                    color: '#ffffff'
                  }}>
                  Profile
                </Link>
                <Link to="/whitepaper" className="px-4 py-3 rounded-lg text-center transition-colors"
                  style={{
                    color: '#ffffff'
                  }}>
                  Whitepaper
                </Link>
                {/* <Link to="/about" className="px-4 py-3 rounded-lg text-center transition-colors"
                  style={{
                    backgroundColor: currentPath === '/about'
                      ? 'var(--nav-button-active-bg)'
                      : 'var(--nav-button-inactive-bg)',
                    color: currentPath === '/about'
                      ? 'var(--nav-button-active-text)'
                      : 'var(--nav-button-inactive-text)'
                  }}>
                  About
                </Link> */}
              </div>
              <div className='sm:hidden flex flex-row gap-4 items-center mt-6'>
                {/* <button onClick={handleThemeToggle} className="p-2">
                  {theme === 'light' ?
                    <img src={iconDark} className='w-6 h-6' alt="Dark mode" /> :
                    <img src={iconLight} className='w-6 h-6' alt="Light mode" />
                  }
                </button> */}
                {/* <a href="#" target="_blank" className="p-2" rel="noreferrer">
                  <img src={iconX1} className={`w-6 h-6 ${theme === 'light' ? 'filter invert' : ''}`} />
                </a> */}
                <a href="https://t.me/FlazePad" target="_blank" className="p-2" rel="noreferrer">
                  <img src={iconTg1} className={`w-6 h-6 ${theme === 'light' ? 'filter invert' : ''}`} />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TopBar;
